import { ThemeProvider } from "@emotion/react";
import { Box, FormHelperText, TextField, createTheme } from "@mui/material";
import * as React from "react";

const otpTheme = createTheme({
	components: {
		MuiTextField: {
			styleOverrides: {
				root: {
					width: "56px",
					height: "56px",
				},
			},
		},
		MuiOutlinedInput: {
			styleOverrides: {
				root: {
					fontSize: "1rem",
					borderRadius: "12px",
					input: {
						textAlign: "center",
					},
					".MuiOutlinedInput-notchedOutline": {
						borderColor: "#71717A",
					},
					"&:hover .MuiOutlinedInput-notchedOutline": {
						borderColor: "#71717A",
					},
					"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
						borderColor: "#0072CE",
					},
					"&.Mui-error .MuiOutlinedInput-notchedOutline": {
						borderColor: "#DC2626",
					},
				},
			},
		},
		MuiFormHelperText: {
			styleOverrides: {
				root: {
					fontFamily: "Montserrat",
					fontWeight: 500,
					textAlign: "center",
					color: "#DC2626",
					"&.Mui-error": {
						color: "#DC2626",
					},
				},
			},
		},
	},
});

interface OTPProps {
	separator: React.ReactNode;
	length: number;
	value: string;
	disable: boolean;
	errorMessage?: string;
	onChange: React.Dispatch<React.SetStateAction<string>>;
}

function OTP({
	separator,
	length,
	value,
	disable,
	errorMessage,
	onChange,
}: OTPProps) {
	const inputRefs = React.useRef<HTMLInputElement[]>(
		new Array(length).fill(null),
	);

	const focusInput = (targetIndex: number) => {
		const targetInput = inputRefs.current[targetIndex];
		targetInput.focus();
	};

	const selectInput = (targetIndex: number) => {
		const targetInput = inputRefs.current[targetIndex];
		targetInput.select();
	};

	const handleKeyDown = (event: any, currentIndex: number) => {
		switch (event.key) {
			case "ArrowUp":
			case "ArrowDown":
			case " ":
				event.preventDefault();
				break;
			case "ArrowLeft":
				event.preventDefault();
				if (currentIndex > 0) {
					focusInput(currentIndex - 1);
					selectInput(currentIndex - 1);
				}
				break;
			case "ArrowRight":
				event.preventDefault();
				if (currentIndex < length - 1) {
					focusInput(currentIndex + 1);
					selectInput(currentIndex + 1);
				}
				break;
			case "Delete":
				event.preventDefault();
				onChange((prevOtp) => {
					const otp =
						prevOtp.slice(0, currentIndex) + prevOtp.slice(currentIndex + 1);
					return otp;
				});
				break;
			case "Backspace":
				event.preventDefault();
				if (currentIndex > 0) {
					focusInput(currentIndex - 1);
					selectInput(currentIndex - 1);
				}
				onChange((prevOtp) => {
					const otp =
						prevOtp.slice(0, currentIndex) + prevOtp.slice(currentIndex + 1);
					return otp;
				});
				break;
			default:
				break;
		}
	};

	const handleChange = (event: any, currentIndex: number) => {
		const currentValue = event.target.value;
		let indexToEnter = 0;

		while (indexToEnter <= currentIndex) {
			if (
				inputRefs.current[indexToEnter].value &&
				indexToEnter < currentIndex
			) {
				indexToEnter += 1;
			} else {
				break;
			}
		}
		onChange((prev) => {
			const otpArray = prev.split("");
			const lastValue = currentValue[currentValue.length - 1];
			otpArray[indexToEnter] = lastValue;
			return otpArray.join("");
		});
		if (currentValue !== "") {
			if (currentIndex < length - 1) {
				focusInput(currentIndex + 1);
			}
		}
	};

	const handleClick = (currentIndex: number) => {
		selectInput(currentIndex);
	};

	const handlePaste = (event: any, currentIndex: number) => {
		event.preventDefault();
		const clipboardData = event.clipboardData;

		if (clipboardData.types.includes("text/plain")) {
			let pastedText = clipboardData.getData("text/plain");
			pastedText = pastedText.substring(0, length).trim();
			let indexToEnter = 0;

			while (indexToEnter <= currentIndex) {
				if (
					inputRefs.current[indexToEnter].value &&
					indexToEnter < currentIndex
				) {
					indexToEnter += 1;
				} else {
					break;
				}
			}

			const otpArray = value.split("");

			for (let i = indexToEnter; i < length; i += 1) {
				const lastValue = pastedText[i - indexToEnter] ?? " ";
				otpArray[i] = lastValue;
			}

			onChange(otpArray.join(""));
		}
	};

	return (
		<ThemeProvider theme={otpTheme}>
			<Box sx={{ my: 4 }}>
				<Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
					{new Array(length).fill(null).map((_, index) => (
						<React.Fragment key={index as any}>
							<TextField
								variant="outlined"
								error={!!errorMessage}
								disabled={disable}
								inputProps={{
									// @ts-expect-error
									ref: (ele) => {
										inputRefs.current[index] = ele;
									},
									onKeyDown: disable
										? undefined
										: (event) => handleKeyDown(event, index),
									onChange: disable
										? undefined
										: (event) => handleChange(event, index),
									onClick: disable ? undefined : () => handleClick(index),
									onPaste: disable
										? undefined
										: (event) => handlePaste(event, index),
									value: value[index] ?? "",
								}}
							/>
							{index === length - 1 ? null : separator}
						</React.Fragment>
					))}
				</Box>
				{errorMessage && (
					<FormHelperText error={!!errorMessage}>{errorMessage}</FormHelperText>
				)}
			</Box>
		</ThemeProvider>
	);
}

export default OTP;
